import React, { useState } from "react";
import { ArrowRight, CheckCircle2, Loader2, Phone } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import api from "../../lib/api";
import { useHoneypot } from "../../components/Honeypot";
import { MarketingOptIn, OPT_IN_NEEDS_EMAIL } from "../../components/MarketingOptIn";
import { trackEnquiryConversion } from "../../lib/analytics";

const FIELD_LABEL =
  "mb-1.5 block text-[11px] uppercase tracking-wider text-[color:var(--nq-stone)]";

/**
 * Sticky "get a fitted quote" panel.
 *
 * Deliberately short: NQ Springs look up the full vehicle details from the rego,
 * so we ask for the rego instead of make/model/year. Name, phone and rego are
 * mandatory. Posts to the same /api/enquiries endpoint as every other enquiry
 * entry point so the admin inbox stays consistent.
 */
export default function QuoteForm({ page, variants = [], phone }) {
  const [form, setForm] = useState({
    name: "",
    phone: "",
    rego: "",
    email: "",
    interest: "",
    message: "",
  });
  const { honeypot, HoneypotField } = useHoneypot();
  const [optIn, setOptIn] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  async function submit(e) {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim() || !form.rego.trim()) {
      toast.error("Please add your name, phone number and rego");
      return;
    }
    if (optIn && !form.email.trim()) {
      toast.error(OPT_IN_NEEDS_EMAIL);
      return;
    }
    setBusy(true);
    try {
      const payload = {
        name: form.name,
        phone: form.phone,
        vehicle: "",
        rego: form.rego,
        website: honeypot,
        marketing_opt_in: optIn,
        message:
          form.message.trim() ||
          `Please quote ${form.interest || page.title} for rego ${form.rego}.`,
        ref_type: "page",
        ref_id: page.slug,
        ref_slug: page.slug,
        ref_title: form.interest ? `${form.interest} - ${page.title}` : page.title,
        ref_image: page.hero_image || "",
      };
      // Only include email if it's not empty
      if (form.email.trim()) {
        payload.email = form.email;
      }
      const { data } = await api.post("/enquiries", payload);
      setDone(data.enquiry.reference);
      trackEnquiryConversion({ source: "product_quote_form", email: form.email, phone: form.phone });
      toast.success("Quote request sent", { description: "We'll be in touch shortly." });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      let errorMsg = "Please try again.";
      if (typeof detail === "string") {
        errorMsg = detail;
      } else if (Array.isArray(detail) && detail.length > 0) {
        // Pydantic validation error format
        errorMsg = detail.map(e => e.msg || e.message || "Validation error").join(", ");
      }
      toast.error("Could not send", { description: errorMsg });
    } finally {
      setBusy(false);
    }
  }

  if (done) {
    return (
      <div className="card-nq p-6 text-center" data-testid="quote-form-success">
        <CheckCircle2 className="mx-auto h-9 w-9 green-text" />
        <h3 className="mt-3 text-lg font-semibold text-[color:var(--nq-sand)]">Request received</h3>
        <p className="mt-2 text-sm text-[color:var(--nq-stone)]">
          Your reference is{" "}
          <span className="font-mono font-semibold gold-text" data-testid="quote-form-reference">
            {done}
          </span>
          . We&apos;ll confirm fitment and pricing shortly.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="card-nq p-5" data-testid="quote-form">
      {HoneypotField}
      <h3 className="text-lg font-bold text-[color:var(--nq-sand)]">Get a fitted quote</h3>
      <p className="mt-1 text-sm text-[color:var(--nq-stone)]">
        Give us your rego and we&apos;ll look up your vehicle details.
      </p>

      <div className="mt-4 grid grid-cols-2 gap-3">
        <div>
          <label className={FIELD_LABEL} htmlFor="quote-name">
            Your name *
          </label>
          <Input
            id="quote-name"
            value={form.name}
            onChange={set("name")}
            required
            data-testid="quote-name-input"
          />
        </div>
        <div>
          <label className={FIELD_LABEL} htmlFor="quote-phone">
            Phone *
          </label>
          <Input
            id="quote-phone"
            value={form.phone}
            onChange={set("phone")}
            required
            inputMode="tel"
            data-testid="quote-phone-input"
          />
        </div>
      </div>

      <div className="mt-3">
        <label className={FIELD_LABEL} htmlFor="quote-rego">
          Rego number *
        </label>
        <Input
          id="quote-rego"
          value={form.rego}
          onChange={set("rego")}
          required
          placeholder="We'll look up your vehicle from this"
          data-testid="quote-rego-input"
        />
      </div>

      <div className="mt-3">
        <label className={FIELD_LABEL} htmlFor="quote-email">
          Email
        </label>
        <Input
          id="quote-email"
          type="email"
          value={form.email}
          onChange={set("email")}
          data-testid="quote-email-input"
        />
      </div>

      {variants.length > 0 && (
        <div className="mt-3">
          <label className={FIELD_LABEL}>Product interest</label>
          <Select value={form.interest} onValueChange={(v) => setForm((f) => ({ ...f, interest: v }))}>
            <SelectTrigger data-testid="quote-interest-select">
              <SelectValue placeholder="Select option" />
            </SelectTrigger>
            <SelectContent>
              {variants.map((v) => (
                <SelectItem key={v.name} value={v.name}>
                  {v.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      <div className="mt-3">
        <label className={FIELD_LABEL} htmlFor="quote-message">
          How can we help?
        </label>
        <Textarea
          id="quote-message"
          rows={3}
          value={form.message}
          onChange={set("message")}
          placeholder="Tell us about your build"
          data-testid="quote-message-input"
        />
      </div>

      {phone && (
        <div
          className="mt-4 rounded-lg border p-3"
          style={{ borderColor: "rgba(198,162,74,0.22)", background: "rgba(198,162,74,0.05)" }}
        >
          <p className="text-xs text-[color:var(--nq-stone)]">Not sure what fits? Call the team.</p>
          <a
            href={`tel:${phone.replace(/[^\d+]/g, "")}`}
            className="mt-1 flex items-center gap-2 text-base font-bold gold-text transition-opacity duration-200 hover:opacity-80"
            data-testid="quote-form-phone"
          >
            <Phone className="h-4 w-4" /> {phone}
          </a>
        </div>
      )}

      <MarketingOptIn id="quote-marketing-opt-in" checked={optIn} onChange={setOptIn} />

      <button
        type="submit"
        disabled={busy}
        className="btn-gold mt-4 w-full disabled:opacity-60"
        data-testid="quote-submit-button"
      >
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
        {busy ? "Sending" : "Request my quote"}
        {!busy && <ArrowRight className="h-4 w-4" />}
      </button>
    </form>
  );
}
